import { useContext, useEffect, useRef, useState, useSyncExternalStore, useCallback, memo, useMemo } from 'react';
import { MobileGamepadInputStandalone, MobileGamepadUi } from '@playcastdotio/MobileGamepad';
import type { MobileGamepadInputEvent } from '@playcastdotio/MobileGamepad';
import { setGamepadState, setTouchTargetElement, setContentAspectRatio } from '../../api/modules/hostMessaging/inputEvents';
import { captureStore, useAnimationFrame } from '@playcastdotio/InputCapture';
import { useInputCaptureManager } from './useInputCaptureManager';
import { Overlay } from './overlay/Overlay';
import clsx from 'clsx';
import { PlayerStats } from './PlayerStats';
import { PCCountdownTimer } from './PCCountdownTimer';
import { PlaycastContext } from '../PlayerContext';
import { getPeer, playerDisconnect } from '../../api/modules/peer';
import {
  mediaStreamState,
  pauseInputState,
  playerStaticImageState,
  showStaticImageState,
  gamepadState,
  playerInfoState,
  truncMachineIdState,
} from '../../api/stores/playerStores';
import { invitationState } from '../../api/stores/lobbyStores';
import { useIsMobileDevice } from '../../utility/utility';
import { useShouldShowMobileGamepad } from '@playcastdotio/SharedComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause } from '@fortawesome/duotone-thin-svg-icons/faPause';
import { PlaycastGamepadId } from '@playcastdotio/CoreTypes';
import { faGamepadAlt } from '@fortawesome/pro-solid-svg-icons';
import { NotificationMessageType } from '@playcastdotio/Messaging';
import { sendMessage } from '../../api/modules/realtimeMessaging';
import { websocket } from '@playcastdotio/Messaging';
import {
  sendAnalytics,
  PlaycastAnalyticsEmailSubmitted,
  PlaycastAnalyticsEmailSent,
  PlaycastAnalyticsUserExited,
} from '@playcastdotio/Analytics';
import { getPersistentPlayerId } from '../../api';
import { generators } from '@playcastdotio/Messaging';
import { authStore } from '../../api/stores/globalStores';
import * as Sentry from '@sentry/react';

const sendVideoDecodeTimingData = (stage: 'video_decode') => {
  const baseMessage = {
    header: {
      source: 'player' as const,
      target: 'system' as const,
      action: 'videoDecodeTiming' as const,
      isReply: false,
      hostId: '',
      userAuth: authStore.currentState.playerAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: `video_${stage}`,
    },
    'player',
    {
      stage: stage,
      decode_timestamp: performance.now().toString(),
    },
  );

  console.log('[Player] Video decode timing data:', {
    stage,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};

const sendVideoRenderTimingData = (stage: 'video_render') => {
  const baseMessage = {
    header: {
      source: 'player' as const,
      target: 'system' as const,
      action: 'videoRenderTiming' as const,
      isReply: false,
      hostId: '',
      userAuth: authStore.currentState.playerAuthId,
    },
    body: {
      message: {},
      timings: [],
      stats: [],
      echo: false,
    },
    signature: '',
  } as any;

  const messageWithTiming = generators.addTiming(
    baseMessage,
    {
      timestamp: performance.now(),
      description: `video_${stage}`,
    },
    'player',
    {
      stage: stage,
      render_timestamp: performance.now().toString(),
    },
  );

  console.log('[Player] Video render timing data:', {
    stage,
    timestamp: Date.now(),
    timingMessage: messageWithTiming,
  });

  // TODO: send timing data to BigQuery
};

const base64ToBlob = (base64: string): Blob => {
  const byteString = atob(base64.split(',')[1]);
  const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};

const GamepadIndicator = memo(({ displayName, gamepadSlot }: { displayName: string; gamepadSlot: number }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className={clsx(
        'flex items-center gap-2 rounded-xl bg-black/30 p-2 transition-opacity duration-500',
        isVisible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div className='relative'>
        <FontAwesomeIcon icon={faGamepadAlt} className='size-8 text-emerald-400' />
        <span className='text-stroke-1 text-stroke-emerald-400 absolute -top-1.5 left-1/2 -translate-x-1/2 text-2xl font-bold text-black'>
          {gamepadSlot + 1}
        </span>
      </div>
      <span className='text-white'>{displayName} has joined</span>
    </div>
  );
});

const GamepadSlots = memo(({ playerMappings }: { playerMappings: Record<string, { gamepadIndexes: number[] }> }) => {
  const [isVisible, setIsVisible] = useState(true);
  const occupiedSlots = useMemo(() => {
    const slots = new Set<number>();
    Object.values(playerMappings).forEach((mapping) => {
      mapping.gamepadIndexes.forEach((index) => slots.add(index));
    });
    return slots;
  }, [playerMappings]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={clsx('flex w-fit gap-2 rounded-xl bg-black/30 p-2', isVisible ? 'opacity-100' : 'opacity-0')}>
      {[0, 1, 2, 3].map((slot) => (
        <FontAwesomeIcon
          key={slot}
          icon={faGamepadAlt}
          className={clsx('size-8', occupiedSlots.has(slot) ? 'text-emerald-400' : 'text-gray-500')}
        />
      ))}
    </div>
  );
});

// Memoize the base component to prevent unnecessary re-renders
interface PlayerContentProps {
  customButtonHandler?: () => void;
  customButtonText?: string;
  isBrainrotMode?: boolean; // Add prop to indicate brainrot mode
  isQuickstartMode?: boolean; // Add prop to indicate quickstart mode
}

const PlayerContent = memo(({ customButtonHandler, customButtonText, isBrainrotMode = false, isQuickstartMode = false }: PlayerContentProps) => {
  const playerId = getPeer()?.peerId;
  const data = useContext(PlaycastContext);
  const access = useSyncExternalStore(captureStore.subscribeAccess, captureStore.getAccess);
  const [isPhysicalGamepadConnected, setIsPhysicalGamepadConnected] = useState(false);
  const [machineIdText, setMachineIdText] = useState('');

  // Reactive mobile detection - updates when viewport changes (e.g., "Request Desktop Site")
  const isMobileDevice = useIsMobileDevice();
  // Touch-based detection for mobile gamepad - detects actual touch input vs mouse clicks
  // This handles iPads that show desktop UI but still need touch controls
  const shouldShowMobileGamepad = useShouldShowMobileGamepad();

  const isLegacyPlayerActive = data.mode.mode === 'player' && !isQuickstartMode;
  useInputCaptureManager({ access, active: isLegacyPlayerActive });

  const handleMobileGamepadInput = useCallback((event: MobileGamepadInputEvent) => {
    if (event.gamepadState) {
      setGamepadState(event.gamepadState);
    }
  }, []);

  // Track user interactions with simple debouncing
  const lastUnmuteAttemptRef = useRef(0);
  const unmuteDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get invitation data for softkick countdown timer
  const [invitationData, setInvitationData] = useState<{ softKickExpirationTime?: number }>({});

  const checkInvitationData = useCallback(() => {
    try {
      const currentInvitationState = invitationState.currentState;
      console.log('Checking invitation data:', currentInvitationState);

      // Check for selected invitation in invitationState
      if (currentInvitationState?.selectedInvite && typeof currentInvitationState.selectedInvite === 'object') {
        console.log('Found selectedInvite:', currentInvitationState.selectedInvite);
        if (
          currentInvitationState.selectedInvite?.type === 'cafe' &&
          currentInvitationState.selectedInvite.manifest?.softKickExpirationTime
        ) {
          const softKickExpirationTime = currentInvitationState.selectedInvite.manifest.softKickExpirationTime;
          console.log('Setting softKickExpirationTime from selectedInvite:', softKickExpirationTime);
          setInvitationData({
            softKickExpirationTime,
          });
        } else {
          console.log('No softKickExpirationTime in selectedInvite');
          setInvitationData({});
        }
      } else if (currentInvitationState?.invites && Object.keys(currentInvitationState.invites).length > 0) {
        // If no selectedInvite, check the invites object for the current invitation
        const inviteIds = Object.keys(currentInvitationState.invites);
        console.log('Found invites:', inviteIds);

        // Get the first invitation from the invites object
        const firstInviteId = inviteIds[0];
        const currentInvitation = currentInvitationState.invites[firstInviteId];
        console.log('Current invitation:', currentInvitation);

        if (
          currentInvitation &&
          typeof currentInvitation === 'object' &&
          currentInvitation?.type === 'cafe' &&
          currentInvitation.manifest?.softKickExpirationTime
        ) {
          const softKickExpirationTime = currentInvitation.manifest.softKickExpirationTime;
          console.log('Setting softKickExpirationTime from invites:', softKickExpirationTime);
          setInvitationData({
            softKickExpirationTime,
          });
        } else {
          console.log('No softKickExpirationTime in currentInvitation');
          setInvitationData({});
        }
      } else {
        console.log('No invitation data found');
        setInvitationData({});
      }
    } catch (error) {
      console.error('Error accessing invitation data:', error);
    }
  }, []);

  useEffect(() => {
    // Set up listener for invitation state changes
    const invitationListener = invitationState.addListener(() => {
      checkInvitationData();
    });

    // Also check once on mount
    checkInvitationData();

    return () => {
      invitationState.removeListener(invitationListener);
    };
  }, [checkInvitationData]);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const isIdleRef = useRef(false);
  const [showIdleUI, setShowIdleUI] = useState(false);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const autoKickTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Add auto-kick timeout ref
  const [gamepadSlot, setGamepadSlot] = useState<PlaycastGamepadId>(-1);

  // Consolidate stream-related states into a single object
  const [streamState, setStreamState] = useState({
    hasAudio: false,
    hasVideo: false,
    isMuted: true,
    isPlaying: false,
    isVisible: true,
  });

  const [isVerticalStream, setIsVerticalStream] = useState(false);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isStatsOpen, setIsStatsOpen] = useState(false);

  const videoFrame = useRef<HTMLVideoElement>(null);
  const setVideoFrameRef = useCallback((element: HTMLVideoElement | null) => {
    (videoFrame as React.MutableRefObject<HTMLVideoElement | null>).current = element;
    setTouchTargetElement(element);
  }, []);
  const audioFrame = useRef<HTMLAudioElement>(null);
  const currentIsGamepadConnectedRef = useRef(false);
  const previousStreamRef = useRef<{ stream: MediaStream | null; audioStream: MediaStream | null }>({ stream: null, audioStream: null });
  const previousVideoStreamCount = useRef(0);
  const previousStreamStateRef = useRef(streamState);
  const staticImageRef = useRef<string | null>(null);
  const lastAppliedStreamRef = useRef<MediaStream | null>(null);

  const hasStreamUpdate = useRef(false);

  // Track consecutive pause state check failures for Sentry reporting
  const pauseCheckFailuresRef = useRef(0);
  const lastPauseCheckErrorRef = useRef<{ error: { name: string; message: string }; timestamp: number } | null>(null);

  const [newPlayers, setNewPlayers] = useState<Array<{ id: string; displayName: string; gamepadSlot: number }>>([]);
  const previousPlayersRef = useRef<Record<string, { displayName: string; gamepadSlots: number[] }>>({});

  const resetIdleTimeout = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    // Clear auto-kick timeout if in brainrot mode
    if (isBrainrotMode && autoKickTimeoutRef.current) {
      clearTimeout(autoKickTimeoutRef.current);
    }

    // Only update UI state if we're transitioning from idle to active
    if (isIdleRef.current) {
      isIdleRef.current = false;
      setShowIdleUI(false);
    }

    idleTimeoutRef.current = setTimeout(() => {
      isIdleRef.current = true;
      if (isFullscreen) {
        setShowIdleUI(true);
      }
    }, 3000); // 3 seconds

    // Set auto-kick timeout only in brainrot mode
    if (isBrainrotMode) {
      autoKickTimeoutRef.current = setTimeout(
        () => {
          console.log('Auto-kicking user due to 10 minutes of inactivity');
          // Hard navigate to root URL
          window.location.href = '/';
        },
        25 * 60 * 1000,
      ); // 25 minutes
    }
  }, [isFullscreen, isBrainrotMode]);

  const attemptUnmute = useCallback(() => {
    const now = Date.now();
    const timeSinceLastAttempt = now - lastUnmuteAttemptRef.current;

    // Debounce to prevent spam (500ms minimum between attempts)
    if (timeSinceLastAttempt < 500) {
      console.log('🎯 Unmute attempt debounced');
      return;
    }

    lastUnmuteAttemptRef.current = now;

    try {
      const isAndroid = /Android/.test(navigator.userAgent);
      console.log('🎯 User interaction - checking and attempting to unmute');

      // In Quickstart mode, video element stays muted; only audio element is controlled
      if (!isQuickstartMode && videoFrame.current && videoFrame.current.muted) {
        videoFrame.current.muted = false;
        videoFrame.current.play().catch(() => undefined);
        console.log('📺 Video unmuted');
      }

      if (audioFrame.current && audioFrame.current.muted) {
        const audioStream = audioFrame.current.srcObject as MediaStream | null;

        if (audioStream && audioStream.getAudioTracks().length > 0) {
          const audioTracks = audioStream.getAudioTracks();
          console.log(
            '🎵 Attempting to unmute audio with tracks:',
            audioTracks.map((t) => ({
              id: t.id,
              label: t.label,
              enabled: t.enabled,
              readyState: t.readyState,
              muted: t.muted,
            })),
          );

          // For Android, refresh the stream connection
          if (isAndroid) {
            console.log('🤖 Android: Refreshing audio stream');
            const currentSrc = audioFrame.current.srcObject;
            audioFrame.current.srcObject = null;
            setTimeout(() => {
              if (audioFrame.current && currentSrc) {
                audioFrame.current.srcObject = currentSrc;
                audioFrame.current.muted = false;
                audioFrame.current.volume = 1.0;
                audioFrame.current.play().catch(() => undefined);
              }
            }, 100);
          } else {
            audioFrame.current.muted = false;
            audioFrame.current.volume = 1.0;
            audioFrame.current.play().catch(() => undefined);
          }

          // Update stream state
          setStreamState((prev) => ({ ...prev, isMuted: false }));

          // Verify audio element state after unmute (especially for Quickstart)
          setTimeout(() => {
            if (audioFrame.current) {
              console.log('🔊 Audio unmuted - verification:', {
                isQuickstart: isQuickstartMode,
                muted: audioFrame.current.muted,
                paused: audioFrame.current.paused,
                volume: audioFrame.current.volume,
                readyState: audioFrame.current.readyState,
                currentTime: audioFrame.current.currentTime,
                hasAudioTracks: (audioFrame.current.srcObject as MediaStream)?.getAudioTracks()?.length || 0,
              });
            }
          }, 200);
        } else {
          console.log('⏳ No audio tracks available yet');
        }
      } else {
        console.log('🔊 Audio already unmuted or no audio element');
      }
    } catch (error) {
      console.error('🎵 Error during unmute attempt:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // isQuickstartMode is constant prop, safe to omit from deps

  const attemptPlayVideo = useCallback(() => {
    // Try to play video if it's paused (for Android where video pauses unexpectedly)
    // This doesn't interfere with mobile gamepad since it only calls play() on the video element
    const video = videoFrame.current;
    if (video && video.paused && streamState.isVisible) {
      const isAndroid = /Android/.test(navigator.userAgent);
      console.log('🎯 User interaction - attempting to play paused video', {
        isAndroid,
        readyState: video.readyState,
        networkState: video.networkState,
      });

      video.play()
        .then(() => {
          console.log('✅ Video play triggered by user interaction');
        })
        .catch((error) => {
          console.log('⚠️ Video play failed on user interaction:', error.name, error.message);
        });
    }
  }, [streamState.isVisible]);

  const onFirstInteraction = useCallback(() => {
    attemptUnmute();
    attemptPlayVideo();
  }, [attemptUnmute, attemptPlayVideo]);

  // Stable refs so the gesture handler useEffect ([] deps) always calls latest versions
  const attemptPlayVideoRef = useRef(attemptPlayVideo);
  const attemptUnmuteRef = useRef(attemptUnmute);
  const resetIdleTimeoutRef = useRef(resetIdleTimeout);
  attemptPlayVideoRef.current = attemptPlayVideo;
  attemptUnmuteRef.current = attemptUnmute;
  resetIdleTimeoutRef.current = resetIdleTimeout;

  const handleGamepadCheck = useCallback(() => {
    const g = navigator.getGamepads();
    const hasGamepad = !!g.some((element) => element instanceof Gamepad);

    // Only update state if the value has changed
    if (hasGamepad !== currentIsGamepadConnectedRef.current) {
      currentIsGamepadConnectedRef.current = hasGamepad;
      setIsPhysicalGamepadConnected(hasGamepad);
    }

    // Check for gamepad activity
    if (hasGamepad) {
      const gamepad = g[0];
      if (gamepad) {
        // Check if any button is pressed or any axis is moved
        const hasButtonPress = gamepad.buttons.some((button) => button.pressed);
        const hasAxisMovement = gamepad.axes.some((axis) => Math.abs(axis) > 0.1);
        if (hasButtonPress || hasAxisMovement) {
          resetIdleTimeout();
        }
      }
    }
  }, [resetIdleTimeout]);

  useAnimationFrame(handleGamepadCheck);

  const updateStreamState = useCallback((stream: MediaStream | null, audioStream: MediaStream | null) => {
    // Check if streams have actually changed

    if (stream) {
      const videoTracks = stream.getVideoTracks();

      if (
        previousStreamRef.current.stream === stream &&
        previousStreamRef.current.audioStream === audioStream &&
        previousVideoStreamCount.current === videoTracks.length
      ) {
        return;
      }

      // Update previous stream ref
      previousStreamRef.current = { stream, audioStream };
      previousVideoStreamCount.current = videoTracks.length;

      // Detect vertical (portrait) streams via the track's actual dimensions
      const primaryVideoTrack = videoTracks[0];
      if (primaryVideoTrack) {
        const settings = primaryVideoTrack.getSettings();
        const vertical = (settings.height ?? 0) > (settings.width ?? 0);
        setIsVerticalStream(vertical);
        setContentAspectRatio(vertical ? 9 / 16 : 16 / 9);
      }

      const audioTracks = audioStream ? audioStream.getAudioTracks() : [];

      const isVisible = !hasStreamUpdate.current || videoTracks.length > 0;
      hasStreamUpdate.current = true;

      const newState = {
        hasVideo: videoTracks.length > 0,
        hasAudio: audioTracks.length > 0,
        isVisible: isVisible,
        isMuted: true,
        isPlaying: true, // Always true since video autoplays
      };

      // Only update if state has actually changed
      if (JSON.stringify(previousStreamStateRef.current) !== JSON.stringify(newState)) {
        previousStreamStateRef.current = newState;
        setStreamState(newState);
      }

      if (videoFrame.current) {
        // For iOS, ensure we're setting srcObject with a clean approach
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

        // Clear any existing srcObject first
        if (isIOS && videoFrame.current.srcObject) {
          videoFrame.current.srcObject = null;
        }

        videoFrame.current.srcObject = stream;
        videoFrame.current.autoplay = true;
        videoFrame.current.playsInline = true;
        videoFrame.current.muted = true;

        // Remove the problematic event handlers that were causing setState loops
        // The video will autoplay, and we don't need to track playing state for functionality
      }

      if (audioFrame.current && audioTracks.length > 0) {
        // Set up audio element with audio stream
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
        const isAndroid = /Android/.test(navigator.userAgent);

        // For mobile devices (iOS/Android), ensure we're setting srcObject with a clean approach
        if ((isIOS || isAndroid) && audioFrame.current.srcObject) {
          audioFrame.current.srcObject = null;
        }

        // Use the dedicated audioStream if available, otherwise use the main stream
        const audioStreamToUse = audioStream || stream;
        if (audioStreamToUse && audioStreamToUse.getAudioTracks().length > 0) {
          const audioTracks = audioStreamToUse.getAudioTracks();
          console.log('🎵 Setting up audio element with tracks (supports multiple):', {
            trackCount: audioTracks.length,
            tracks: audioTracks.map((t) => ({
              id: t.id,
              label: t.label,
              enabled: t.enabled,
              readyState: t.readyState,
              kind: t.kind,
            })),
          });

          // Clear previous srcObject and event listeners to prevent conflicts
          if (audioFrame.current.srcObject) {
            console.log('🗑️ Clearing previous audio srcObject');
            audioFrame.current.srcObject = null;
            audioFrame.current.onplay = null;
            audioFrame.current.onpause = null;
            audioFrame.current.onerror = null;
            audioFrame.current.oncanplay = null;
            audioFrame.current.onloadedmetadata = null;
          }

          audioFrame.current.srcObject = audioStreamToUse;
          audioFrame.current.autoplay = true;
          audioFrame.current.muted = true; // Start muted for autoplay policy compliance
          audioFrame.current.volume = 1.0;

          // Add event listeners for audio playback state
          audioFrame.current.onplay = () => {
            console.log('🎵 Audio started playing');
          };
          audioFrame.current.onpause = () => {
            console.log('⏸️ Audio paused');
          };
          audioFrame.current.onerror = (e) => {
            console.warn('❌ Audio playback error:', e);
          };

          // Simple audio monitoring
          audioFrame.current.oncanplay = () => {
            console.log('🎵 Audio can play - ready for playback');
          };

          audioFrame.current.onplay = () => {
            console.log('🎵 Audio started playing');
          };

          audioFrame.current.onpause = () => {
            console.log('⏸️ Audio paused');
          };

          audioFrame.current.onloadedmetadata = () => {
            console.log('🎵 Audio metadata loaded');
          };

          // For Android specifically, try to handle track changes more gracefully
          if (isAndroid) {
            // Add a small delay to ensure proper stream attachment on Android
            setTimeout(() => {
              if (audioFrame.current && audioFrame.current.paused) {
                console.log('🤖 Android: Attempting delayed audio play');
                audioFrame.current.play().catch(() => undefined);
              }
            }, 100);
          }
        } else {
          console.log('🚫 No audio tracks to set up, clearing audio element');
          if (audioFrame.current.srcObject) {
            audioFrame.current.srcObject = null;
          }
        }
      }
    } else {
      const newState = {
        hasVideo: false,
        hasAudio: false,
        isVisible: false,
        isPlaying: false,
        isMuted: true,
      };

      // Only update if state has actually changed
      const stateChanged =
        !previousStreamStateRef.current || previousStreamStateRef.current.hasVideo || previousStreamStateRef.current.hasAudio;

      if (stateChanged) {
        previousStreamStateRef.current = newState;
        setStreamState(newState);
      }
    }
  }, []); // Removed dependencies to prevent loops

  // Simplified stream handling without dependency loops
  useEffect(() => {
    let isDestroyed = false;

    // Check initial state when component mounts
    const currentState = mediaStreamState.currentState;
    if (currentState.stream || currentState.audioStream) {
      updateStreamState(currentState.stream, currentState.audioStream);
    }

    const listener = mediaStreamState.addListener((state) => {
      if (isDestroyed) return;

      const audioTracks = state.audioStream ? state.audioStream.getAudioTracks() : [];
      console.log('🔄 Media stream updated:', {
        hasStream: !!state.stream,
        hasAudioStream: !!state.audioStream,
        audioTrackCount: audioTracks.length,
        trackDetails: audioTracks.map((t) => ({
          id: t.id,
          label: t.label,
          enabled: t.enabled,
          readyState: t.readyState,
          muted: t.muted,
          kind: t.kind,
        })),
      });

      updateStreamState(state.stream, state.audioStream);
    });

    return () => {
      isDestroyed = true;
      mediaStreamState.removeListener(listener);

      // Clean up debounce timeout
      if (unmuteDebounceTimeoutRef.current) {
        clearTimeout(unmuteDebounceTimeoutRef.current);
        unmuteDebounceTimeoutRef.current = null;
      }
    };
  }, []); // No dependencies

  // Handle static image separately with its own effect
  useEffect(() => {
    let isDestroyed = false;
    let currentImageSrc: string | null = null;

    const updateImage = (base64: string | null) => {
      if (isDestroyed) return;

      // Clean up previous blob URL
      if (currentImageSrc) {
        URL.revokeObjectURL(currentImageSrc);
        currentImageSrc = null;
      }

      if (base64) {
        currentImageSrc = URL.createObjectURL(base64ToBlob(base64));
        setImageSrc(currentImageSrc);
      } else {
        setImageSrc(null);
      }
    };

    // Set initial image
    const initialImage = playerStaticImageState.currentState.staticImageBase64;
    if (initialImage) {
      updateImage(initialImage);
      staticImageRef.current = initialImage;
    }

    const imageListener = playerStaticImageState.addListener((state) => {
      if (isDestroyed || state.staticImageBase64 === staticImageRef.current) return;

      console.log('📸 Static image changed');
      staticImageRef.current = state.staticImageBase64;
      updateImage(state.staticImageBase64);
    });

    return () => {
      isDestroyed = true;
      playerStaticImageState.removeListener(imageListener);

      // Clean up current blob URL
      if (currentImageSrc) {
        URL.revokeObjectURL(currentImageSrc);
      }
    };
  }, []); // No dependencies

  // Handle static image visibility separately
  useEffect(() => {
    let isDestroyed = false;

    const staticImageVisibilityListener = showStaticImageState.addListener((state) => {
      if (isDestroyed) return;

      setStreamState((prev) => {
        if (prev.isVisible === !state.showStaticImage) return prev;
        return { ...prev, isVisible: !state.showStaticImage };
      });
    });

    return () => {
      isDestroyed = true;
      showStaticImageState.removeListener(staticImageVisibilityListener);
    };
  }, []); // No dependencies

  useEffect(() => {
    const tryPlayMuted = async () => {
      try {
        if (videoFrame.current) {
          await videoFrame.current.play().catch(() => undefined);
        }
        if (audioFrame.current) {
          await audioFrame.current.play().catch(() => undefined);
        }
      } catch {
        // ignore
      }
    };

    // Functions now defined as useCallback above

    // Attempt initial autoplay with muted media
    tryPlayMuted();

    // One-time gesture listeners with proper cleanup
    const handleInteraction = () => {
      onFirstInteraction();
    };

    // Use { once: true } to automatically remove listeners and prevent memory leaks
    window.addEventListener('pointerdown', handleInteraction, { once: true, passive: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });
    window.addEventListener('click', handleInteraction, { once: true, passive: true });
    window.addEventListener('keydown', handleInteraction, { once: true, passive: true });

    return () => {
      // Cleanup in case component unmounts before first interaction
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('click', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, []); // No dependencies needed

  useEffect(() => {
    if (streamState.isVisible) {
      videoFrame.current?.style.setProperty('display', 'block');
      // videoFrame.current?.play();
    } else {
      videoFrame.current?.style.setProperty('display', 'none');
      // videoFrame.current?.pause();
    }
  }, [streamState.isVisible]);

  // Periodically ensure the current MediaStream is applied to the video element and playing
  useEffect(() => {
    const intervalMs = 1500;

    // Helper to retry video.play() with exponential backoff
    const retryVideoPlay = async (video: HTMLVideoElement, maxRetries = 3) => {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          console.log(`🎬 Attempting to resume paused video (attempt ${attempt + 1}/${maxRetries})`, {
            paused: video.paused,
            readyState: video.readyState,
            networkState: video.networkState,
            currentTime: video.currentTime,
            timestamp: Date.now()
          });

          await video.play();

          console.log('✅ Video resumed successfully', {
            paused: video.paused,
            readyState: video.readyState,
            currentTime: video.currentTime,
            timestamp: Date.now(),
            attempts: attempt + 1
          });
          return; // Success - exit retry loop

        } catch (error: any) {
          const isLastAttempt = attempt === maxRetries - 1;

          console.warn(`${isLastAttempt ? '🚨' : '⚠️'} Video play() failed (attempt ${attempt + 1}/${maxRetries}):`, {
            errorName: error.name,
            errorMessage: error.message,
            paused: video.paused,
            readyState: video.readyState,
            networkState: video.networkState,
            srcObject: !!video.srcObject,
            timestamp: Date.now()
          });

          // Don't retry certain errors (user aborted, etc)
          if (error.name === 'AbortError' || error.name === 'NotSupportedError') {
            console.log('🛑 Non-retryable error, stopping retry attempts');
            return;
          }

          // If not last attempt, wait with exponential backoff before retry
          if (!isLastAttempt) {
            const backoffMs = Math.min(100 * Math.pow(2, attempt), 1000); // 100ms, 200ms, 400ms, capped at 1s
            console.log(`⏳ Waiting ${backoffMs}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, backoffMs));
          }
        }
      }
    };

    const tick = () => {
      const video = videoFrame.current;
      const audio = audioFrame.current;
      const stream = mediaStreamState.currentState.stream;
      const audioStream = mediaStreamState.currentState.audioStream;

      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
      const isAndroid = /Android/.test(navigator.userAgent);

      // Handle video element
      if (video && stream) {
        const hasVideo = stream.getVideoTracks().length > 0;
        if (hasVideo) {
          // Re-apply stream if missing or changed
          if (video.srcObject !== stream || lastAppliedStreamRef.current !== stream) {
            try {
              if ((isIOS || isAndroid) && video.srcObject) {
                video.srcObject = null;
              }
              video.srcObject = stream;
              video.autoplay = true;
              video.playsInline = true;
              video.muted = isQuickstartMode ? true : streamState.isMuted; // Quickstart: video always muted, audio element controls sound
              lastAppliedStreamRef.current = stream;
            } catch {
              // ignore
            }
          }

          // Only try to resume if the video is actually paused (not just buffering)
          if (streamState.isVisible && video.paused) {
            // Use retry logic with more attempts on Android where pausing is more common
            const maxRetries = isAndroid ? 5 : 3;
            retryVideoPlay(video, maxRetries).catch(() => {
              // Final catch to ensure we never throw from async function
            });
          }
        }
      }

      // Handle audio element - especially important for Android
      if (audio && audioStream) {
        const hasAudio = audioStream.getAudioTracks().length > 0;
        if (hasAudio) {
          // Check if audio stream has changed and needs reattachment
          if (audio.srcObject !== audioStream) {
            console.log('🔄 Audio stream changed, reattaching to element');
            console.log('  Previous srcObject:', audio.srcObject ? 'present' : 'missing');
            console.log(
              '  New audioStream tracks:',
              audioStream.getAudioTracks().map((t) => ({ id: t.id, label: t.label, enabled: t.enabled })),
            );

            audio.srcObject = audioStream;
            // Preserve mute state from streamState, not from previous audio element
            audio.muted = streamState.isMuted;
            audio.volume = 1.0;

            if (!streamState.isMuted) {
              console.log('🔊 Audio should be unmuted, attempting to play');
              audio.play().catch(() => undefined);
            }
          }

          // Ensure audio is playing when it should be (especially important for Android and Quickstart)
          if (!streamState.isMuted && audio.paused && audio.readyState >= 2) {
            console.log(`${isAndroid ? '🤖 Android' : '🔊'}: Audio should be playing but is paused, attempting to resume`, {
              isQuickstart: isQuickstartMode,
              readyState: audio.readyState,
              hasAudioTracks: audioStream.getAudioTracks().length,
              currentTime: audio.currentTime,
            });
            audio.play().catch((err) => {
              console.warn('⚠️ Failed to resume audio:', err.name, err.message);
            });
          }

          // Double-check mute state synchronization
          if (audio.muted !== streamState.isMuted) {
            console.log(`${isAndroid ? '🤖 Android' : '🔊'}: Mute state desync detected, fixing...`, {
              audioElementMuted: audio.muted,
              streamStateMuted: streamState.isMuted,
              isQuickstart: isQuickstartMode,
            });
            audio.muted = streamState.isMuted;

            // If we just unmuted, ensure audio is playing
            if (!streamState.isMuted && audio.readyState >= 2) {
              audio.play().catch(() => undefined);
            }
          }
        }
      }
    };

    const id = setInterval(tick, intervalMs);
    // Run once immediately to avoid waiting for the first interval
    tick();
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState.isVisible, streamState.isMuted]); // isQuickstartMode is constant prop, safe to omit from deps

  const handleExitStream = () => {
    playerDisconnect();
  };

  const handleCustomButton = () => {
    // Add your custom button logic here
    console.log('Custom button clicked!');
    // Example: Open a modal, navigate to a page, trigger an action, etc.
  };

  const checkFullscreen = useCallback(() => {
    const isNowFullscreen = !!document.fullscreenElement;
    setIsFullscreen(isNowFullscreen);

    if (isNowFullscreen) {
      resetIdleTimeoutRef.current();
    }
  }, []);

  const handleFullscreenChange = useCallback(() => {
    checkFullscreen();
  }, [checkFullscreen]);


  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Events that count as valid user gestures for unmuting/playing media
    const validGestureEvents = [
      'mousedown',
      'pointerdown',
      'click',
      'dblclick',
      'keypress',
      'keydown',
      'keyup',
      'touchstart',
      'touchend',
      'input',
    ];

    // Events that only count for idle tracking (not valid for unmute)
    const passiveActivityEvents = [
      'mousemove',
      'pointermove',
      'touchmove',
      'scroll',
      'wheel',
      'focus',
    ];

    // Handler for valid gestures - can trigger unmute and play
    const handleValidGesture = () => {
      resetIdleTimeoutRef.current();

      // Only check audio element for mute state -- video element is intentionally
      // muted in quickstart mode, so checking it would always be true.
      if (audioFrame.current?.muted) {
        if (unmuteDebounceTimeoutRef.current) {
          clearTimeout(unmuteDebounceTimeoutRef.current);
        }

        unmuteDebounceTimeoutRef.current = setTimeout(() => {
          attemptUnmuteRef.current();
          unmuteDebounceTimeoutRef.current = null;
        }, 100);
      }

      attemptPlayVideoRef.current();
    };

    // Handler for passive activity - only resets idle timeout
    const handlePassiveActivity = () => {
      resetIdleTimeoutRef.current();
    };

    // Add valid gesture listeners
    validGestureEvents.forEach((eventType) => {
      window.addEventListener(eventType, handleValidGesture, {
        passive: true,
        capture: false,
      });
    });

    // Add passive activity listeners (no unmute attempts)
    passiveActivityEvents.forEach((eventType) => {
      window.addEventListener(eventType, handlePassiveActivity, {
        passive: true,
        capture: false,
      });
    });

    // Initial fullscreen check
    checkFullscreen();
    resetIdleTimeout();

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);

      // Clean up all listeners
      validGestureEvents.forEach((eventType) => {
        window.removeEventListener(eventType, handleValidGesture);
      });

      passiveActivityEvents.forEach((eventType) => {
        window.removeEventListener(eventType, handlePassiveActivity);
      });

      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }

      // Clean up auto-kick timeout
      if (autoKickTimeoutRef.current) {
        clearTimeout(autoKickTimeoutRef.current);
        autoKickTimeoutRef.current = null;
      }

      // Clean up unmute debounce timeout on unmount
      if (unmuteDebounceTimeoutRef.current) {
        clearTimeout(unmuteDebounceTimeoutRef.current);
        unmuteDebounceTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally run once on mount - callbacks are stable via useCallback

  useEffect(() => {
    const listener = gamepadState.addListener((state) => {
      if (state.playerMappings[playerId ?? '']?.gamepadIndexes.length > 0) {
        setGamepadSlot(state.playerMappings[playerId ?? '']?.gamepadIndexes[0]);
      } else {
        setGamepadSlot(-1);
      }
    });
    return () => {
      gamepadState.removeListener(listener);
    };
  }, [playerId]);

  // Video pause state monitoring - checks every 1 second if video is unexpectedly paused
  useEffect(() => {
    const pauseCheckInterval = setInterval(() => {
      const video = videoFrame.current;

      // Only check if we have a video element, stream is visible, and video is paused
      if (!video || !streamState.isVisible || !video.paused) {
        // Reset failure counter if video is playing normally
        if (!video?.paused && pauseCheckFailuresRef.current > 0) {
          console.log('✅ Video resumed successfully, resetting failure counter');
          pauseCheckFailuresRef.current = 0;
          lastPauseCheckErrorRef.current = null;
        }
        return;
      }

      // Video is paused when it should be playing
      const videoState = {
        paused: video.paused,
        readyState: video.readyState,
        networkState: video.networkState,
        ended: video.ended,
        seeking: video.seeking,
        muted: video.muted,
        srcObject: !!video.srcObject,
        hasVideoTracks: (video.srcObject as MediaStream)?.getVideoTracks?.()?.length || 0,
        hasAudioTracks: (video.srcObject as MediaStream)?.getAudioTracks?.()?.length || 0,
        videoTrackState: (video.srcObject as MediaStream)?.getVideoTracks?.()?.[0]?.readyState || 'N/A',
      };

      console.log('⚠️ Video pause detected during monitoring check', videoState);

      // Try to play the video
      video
        .play()
        .then(() => {
          console.log('✅ Video successfully resumed from pause check interval');
          pauseCheckFailuresRef.current = 0;
          lastPauseCheckErrorRef.current = null;
        })
        .catch((error) => {
          pauseCheckFailuresRef.current++;
          const errorInfo = {
            error: {
              name: error.name,
              message: error.message,
            },
            timestamp: Date.now(),
          };
          lastPauseCheckErrorRef.current = errorInfo;

          console.error(
            `❌ Failed to resume video (attempt ${pauseCheckFailuresRef.current}):`,
            error.name,
            error.message,
            videoState
          );

          // Log to Sentry if we've had consecutive failures (threshold: 3 consecutive failures)
          if (pauseCheckFailuresRef.current >= 3) {
            const sentryContext = {
              videoState,
              errorInfo,
              failureCount: pauseCheckFailuresRef.current,
              userAgent: navigator.userAgent,
              isAndroid: /Android/.test(navigator.userAgent),
              isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent),
            };

            console.error('🚨 Logging video pause failure to Sentry', sentryContext);

            Sentry.captureException(new Error('Video cannot be unpaused - invalid track or playback issue'), {
              level: 'warning',
              tags: {
                component: 'Player',
                issue_type: 'video_pause_failure',
                error_name: error.name,
              },
              extra: sentryContext,
            });

            // Reset counter after logging to avoid spam (will re-log if issue persists)
            pauseCheckFailuresRef.current = 0;
          }
        });
    }, 1000); // Check every 1 second

    return () => {
      clearInterval(pauseCheckInterval);
    };
  }, [streamState.isVisible]);

  // Add this effect to track new players
  useEffect(() => {
    const currentPlayers = playerInfoState.currentState.players;
    const previousPlayers = previousPlayersRef.current;

    Object.entries(currentPlayers).forEach(([playerId, playerInfo]) => {
      const prevInfo = previousPlayers[playerId];
      const prevSlots = prevInfo?.gamepadSlots ?? [];
      const currSlots = playerInfo.gamepadSlots ?? [];

      // Show indicator if slots transitioned from empty to non-empty
      if (prevSlots.length === 0 && currSlots.length > 0) {
        setNewPlayers((prev) => {
          if (prev.some((p) => p.id === playerId)) return prev;
          return [
            ...prev,
            {
              id: playerId,
              displayName: playerInfo.displayName,
              gamepadSlot: currSlots[0],
            },
          ];
        });
      }
    });

    previousPlayersRef.current = currentPlayers;
  }, [playerInfoState.currentState.players]);

  // Add cleanup for newPlayers
  useEffect(() => {
    const timer = setTimeout(() => {
      setNewPlayers([]);
    }, 5000);

    return () => clearTimeout(timer);
  }, [newPlayers]);

  // Add debug log for newPlayers state
  useEffect(() => {
    console.log('New Players State:', newPlayers);
  }, [newPlayers]);

  useEffect(() => {
    if(truncMachineIdState.currentState) setMachineIdText(truncMachineIdState.currentState);
    const listener = truncMachineIdState.addListener((state) => setMachineIdText(state));
    return () => { truncMachineIdState.removeListener(listener) }
  }, []);

  return (
    <>
      {isStatsOpen && <PlayerStats />}
      {/* Mobile gamepad only for legacy player mode - Quickstart handles mobile gamepad separately */}
      {/* Uses touch detection to show gamepad - handles iPads in desktop mode that need touch controls */}
      {/* The exit menu strip always shows when on mobile; gamepad controls are gated on access.gamepad */}
      {shouldShowMobileGamepad && data.mode.mode === 'player' && !isQuickstartMode ? (
        <>
          {access.gamepad && !isPhysicalGamepadConnected && <MobileGamepadInputStandalone enabled={true} onInput={handleMobileGamepadInput} />}
          <MobileGamepadUi
            gamepadSlot={gamepadSlot}
            isMouseEnabled={access.mouse}
            isKeyboardEnabled={access.keyboard}
            disconnect={handleExitStream}
            isPhysicalGamepadConnected={isPhysicalGamepadConnected}
            statsToggle={() => setIsStatsOpen(!isStatsOpen)}
            customButtonHandler={customButtonHandler}
            customButtonText={customButtonText}
            hideGamepadControls={!access.gamepad}
          />
        </>
      ) : null}
      <div className='absolute flex h-dvh w-full overflow-hidden'>
        {(pauseInputState.currentState.paused || pauseInputState.currentState.players[playerId ?? '']?.paused) && (
          <div className='fixed left-1/2 top-0 z-50 mt-4 -translate-x-1/2'>
            <FontAwesomeIcon
              icon={faPause}
              className='size-16 text-amber-600'
              style={
                {
                  '--fa-secondary-color': '#ffba00',
                  '--fa-secondary-opacity': '1',
                } as React.CSSProperties
              }
            />
          </div>
        )}

        {/* Countdown Timer - show on all devices */}
        {invitationData.softKickExpirationTime && (
          <PCCountdownTimer expirationTime={invitationData.softKickExpirationTime} isMobile={isMobileDevice} />
        )}

        {/* Gamepad connection indicators - only show in legacy player mode */}
        {!isQuickstartMode && (
          <div className='fixed left-1/2 top-28 z-50 flex -translate-x-1/2 flex-col items-center gap-2'>
            {newPlayers.length > 0 && <GamepadSlots playerMappings={gamepadState.currentState.playerMappings} />}
            {newPlayers.map((player) => (
              <GamepadIndicator key={player.id} displayName={player.displayName} gamepadSlot={player.gamepadSlot} />
            ))}
          </div>
        )}
      </div>
      <div className='absolute flex h-dvh w-full items-end overflow-hidden'>
        {/* Custom ESC notification */}
        {isFullscreen && showIdleUI && !isMobileDevice && (
          <div
            id='fullscreen-notification'
            data-testid='fullscreen-notification'
            className={clsx(
              'fixed left-1/2 top-0 z-50 mt-8 -translate-x-1/2',
              'transform-gpu',
              '[transform:translate(-50%,0)]',
              'animate-notification-slide-down',
              'bg-primary rounded-lg px-4 py-2',
              'shadow-lg shadow-black/10',
            )}
          >
            <span id='fullscreen-notification-text' data-testid='fullscreen-notification-text' className='font-semibold text-gray-900'>
              Hold ESC to exit fullscreen
            </span>
          </div>
        )}
        <div
          className={clsx('h-[100dvh] w-[100vw]', 'bg-content absolute flex items-center justify-center', 'select-none [&_*]:select-none')}
          style={{
            WebkitTouchCallout: 'none',
            touchAction: 'none', // Prevents all touch gestures (pan, zoom, scroll) - replaces need for passive: false
          } as React.CSSProperties}
        >
          {/* {showDebugOverlay && (
            <div className='debugOverlay absolute left-0 top-0 bg-black bg-opacity-50 p-2 text-white' style={{ zIndex: 100 }}>
              <div className='flex flex-row gap-2' style={{ height: '26px' }}>
                <h3>Dev Debug Overlay</h3>
                <Button
                  variant='destructive'
                  size='sm'
                  onClick={() => setShowDebugOverlay(!showDebugOverlay)}
                  style={{ marginLeft: 'auto', transform: 'scale(0.75)', marginTop: '0', marginBottom: '0' }}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              </div>
              <div className='mt-4 flex w-full justify-center border-t border-gray-600 pt-2'>
                <AudioDebugOverlay audioRef={audioFrame} />
              </div>
            </div>
          )} */}
          <Overlay hideOptionsToggle={isQuickstartMode} />

          <video
            ref={setVideoFrameRef}
            className={clsx(
              'bg-content pointer-events-none max-w-screen absolute z-10 max-h-screen object-contain',
              isVerticalStream ? 'aspect-[9/16] h-full' : 'aspect-video w-full',
              streamState.isVisible ? 'visible' : 'hidden',
            )}
            hidden={!streamState.isVisible}
            autoPlay
            muted={isQuickstartMode ? true : streamState.isMuted}
            playsInline
            webkit-playsinline="true"
            x-webkit-airplay="deny"
            disablePictureInPicture
            controlsList="nofullscreen nodownload noremoteplayback"
            draggable={false}
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
            onLoadedData={() => {
              sendVideoDecodeTimingData('video_decode');
            }}
            onCanPlay={() => {
              sendVideoRenderTimingData('video_render');
            }}
            onPause={(e) => {
              const video = e.currentTarget;
              console.warn('🎬 VIDEO PAUSED EVENT', {
                timestamp: new Date().toISOString(),
                currentTime: video.currentTime,
                readyState: video.readyState,
                networkState: video.networkState,
                paused: video.paused,
                ended: video.ended,
                seeking: video.seeking,
                muted: video.muted,
                srcObject: !!video.srcObject,
                hasVideoTracks: (video.srcObject as MediaStream)?.getVideoTracks?.()?.length || 0,
                stackTrace: new Error().stack
              });
            }}
            onPlay={(e) => {
              const video = e.currentTarget;
              console.log('▶️ VIDEO PLAY EVENT', {
                timestamp: new Date().toISOString(),
                currentTime: video.currentTime,
                readyState: video.readyState,
                networkState: video.networkState,
                paused: video.paused,
                muted: video.muted
              });
            }}
          ></video>

          <div className="absolute top-4 left-8 z-20 pointer-events-none rotate-90 origin-top-left">
            <span className="text-white/50 text-sm font-large select-none">{machineIdText}</span>
          </div>

          {/* Audio element - hidden but necessary for audio playback */}
          <audio
            ref={audioFrame}
            autoPlay
            muted={isQuickstartMode ? streamState.isMuted : true} // In Quickstart mode, audio element controls sound; video element is always muted for display only
            style={{ display: 'none' }}
          ></audio>

          {!streamState.isVisible && (
            <div className='bg-content max-w-screen absolute flex h-full max-h-screen w-full items-center justify-center'>
              <img src={imageSrc ? imageSrc : 'images/global-frame.png'} alt='static background' className='h-full w-full object-cover' draggable={false} />
            </div>
          )}
        </div>
      </div>
    </>
  );
});

// Update Player to forward props
export const Player: React.FC<PlayerContentProps> = (props) => <PlayerContent {...props} />;

// Add modal state and handler
export function PlayerWithModal(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleBrainrotClick = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  // Email validation regex (copied from SendInvitePanel)
  const isValidEmail = (email: string): boolean => {
    const regexPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regexPattern.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailSent(false);
  };

  const handleSend = () => {
    if (!isValidEmail(email)) return;
    // Send NOTIFICATION_FORTNITE_REMINDER notification

    sendAnalytics({
      name: 'Email Submitted',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      email: email,
    } as PlaycastAnalyticsEmailSubmitted);

    const outboundMessage = websocket.generators.notification(NotificationMessageType.NOTIFICATION_FORTNITE_REMINDER, { email });
    sendMessage(outboundMessage);
    setEmailSent(true);
    setEmail('');

    sendAnalytics({
      name: 'Email Sent',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      email: email,
    } as PlaycastAnalyticsEmailSent);
  };

  return (
    <>
      {!modalOpen && <Player {...props} customButtonHandler={handleBrainrotClick} customButtonText='Brainrot' isBrainrotMode={true} />}
      {modalOpen && (
        <div className='fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-60'>
          <div
            className='relative mx-4 flex max-h-screen w-full max-w-md flex-col items-center overflow-y-auto rounded-lg p-0 shadow-lg sm:max-h-[90vh]'
            style={{ background: '#26487E' }}
          >
            <button
              type='button'
              onClick={handleCloseModal}
              className='absolute right-2 top-2 text-2xl font-bold text-white hover:text-gray-200 focus:outline-none'
              aria-label='Close'
            >
              ×
            </button>
            {/* Logos Row */}
            <div className='flex w-full flex-row items-center justify-center gap-2 pb-4 pt-8'>
              <img src='/GradientLogo.svg' alt='Logo' className='h-8 w-auto' />
              <img src='/PlaycastPavilionText.svg' alt='Playcast Pavilion' className='h-8 w-auto' />
            </div>
            {/* Promo Image */}
            <a
              href='https://www.fortnite.com/play/island/1514-8773-6943?utm_source=playcast&utm_medium=interactive&utm_campaign=brainrot-july-2025'
              target='_blank'
              rel='noopener noreferrer'
              className='block flex justify-center'
              onClick={() => {
                sendAnalytics({
                  name: 'User Exited',
                  role: 'host',
                  persistentId: getPersistentPlayerId(),
                  destination:
                    'https://www.fortnite.com/play/island/1514-8773-6943?utm_source=playcast&utm_medium=interactive&utm_campaign=brainrot-july-2025',
                } as PlaycastAnalyticsUserExited);
              }}
            >
              <img
                src='/fortnitePromo.webp'
                alt='Fortnite Promo'
                className='mb-4 w-3/4 max-w-xs cursor-pointer rounded transition-opacity hover:opacity-90'
              />
            </a>
            {/* OR */}
            <div className='mb-2 text-lg font-bold text-white'>OR</div>
            {/* Receive a link */}
            <div className='mb-1 text-2xl font-bold text-white'>Receive a link</div>
            {/* Description */}
            <div className='mb-2 px-4 text-center text-base font-normal text-white'>
              We'll send you a link to try MY SINGING BRAINROT 🎤 (NEW) whenever you want
            </div>
            {/* Email input and send button */}
            <div className='mb-4 flex w-full flex-row items-center gap-2 px-4'>
              <input
                type='text'
                placeholder='email@domain.com'
                value={email}
                onChange={handleEmailChange}
                className='flex-1 rounded border border-gray-300 px-3 py-2 text-black focus:border-emerald-600 focus:outline-none'
              />
              <button
                type='button'
                onClick={handleSend}
                className='rounded bg-emerald-600 px-4 py-2 font-bold text-white transition hover:bg-emerald-700 active:bg-emerald-800'
                disabled={!isValidEmail(email)}
              >
                Send Link
              </button>
            </div>
            {emailSent && <div className='mb-4 text-sm text-emerald-200'>Invite sent!</div>}
          </div>
        </div>
      )}
    </>
  );
}
